import { NgView } from '@app/di/decorators';
import { Token } from '@app/di/token';
import { ApiService } from '@app/services/api.service';
import { QuoteService } from '@app/services/quote.service';
import { SearchService } from '@app/services/search.service';
import { StorageService } from '@app/services/storage.service';
import { UploadService } from '@app/services/upload.service';
import { ISearchScope, SearchController } from '@app/views/search.view';
import {
    IFilter,
    IQuote,
    IQuoteFile,
    IQuoteProduct,
    IRootScope,
    ISavedSearch,
    eFieldType,
    eFileSource,
    eFilterControl,
    eFilterSource,
    eFilterType,
    meta,
} from '@models';
import { icons } from '@tools';
import Big from 'big.js';

export interface IQuotesScope extends ISearchScope<IQuote> {
    downloadAttachment: (file: IQuoteFile) => void;
    openProduct: (idQuote: number, p: IQuoteProduct) => void;
    getFirstProductImage: (quote: IQuote) => string;
    // filterJson: string;
    getTotalPrice: (quote: IQuote) => Big;
    fieldChange: (filter: IFilter) => void;
}

@NgView({
    token: Token.QuotesView,
    inherit: [Token.SearchView],
    dependencies: [Token.$RootScope, Token.UploadService, Token.QuoteService, Token.StorageService],
})
export class QuotesController extends SearchController<IQuote> {
    constructor(
        $scope: IQuotesScope,
        agg: SearchService,
        $rootScope: IRootScope,
        private upload: UploadService,
        private quoteService: QuoteService,
        private storageService: StorageService
    ) {
        super($scope, agg);

        agg.drawerService.setHelpUrl('Quotes');
        agg.drawerService.pageType = loc.quotes;
        agg.drawerService.pageIcon = icons.quote;

        $scope.savedSearchEnabled = true;
        this.refreshSavedSearches();

        if (this.agg.$rootScope.company.useDatabaseSearch) {
            $scope.availableFilters.pushArray([this.getWhereFieldFilter()]);
        }
        $scope.binding.search.modelType = meta.Quote.$name;
        $scope.modelTypes = [
            { type: meta.Quote.$name, label: 'Quotes', state: 'kb.quotes' },
            { type: meta.QuoteProduct.$name, label: 'Configured Products', state: 'kb.configuredProducts' },
        ];
        $scope.downloadAttachment = (file: IQuoteFile) => {
            if (file.source == eFileSource.media) {
                window.open(storageService.getMediaUrl(file.filePath));
            } else {
                window.open('api/quotes/file/download/' + file.id, '_blank');
            }
        };
        $scope.openProduct = (idQuote: number, p: IQuoteProduct) => {
            if (p.isConfigured) {
                agg.$state.go('kb.configuredProduct', { id: p.id });
            } else {
                agg.$state.go('kb.product', { id: p.idProduct });
            }
        };
        $scope.getFirstProductImage = (quote: IQuote) => {
            let p = quote.products.find(p => p.imagePath != null);
            return p ? p.imagePath : null;
        };

        $scope.fieldChange = filter => {
            let field = this.$scope.filterSource[filter['$key']].find(f => f.name == filter.fieldName);
            let fieldType = field ? field.type : eFieldType.text;
            filter.fieldType = fieldType;
            if (fieldType == eFieldType.number) {
                filter.control = eFilterControl.numberRange;
            } else if (fieldType == eFieldType.boolean) {
                filter.control = eFilterControl.checkbox;
            } else {
                filter.control = eFilterControl.text;
            }
        };

        agg.drawerService.addDrawer(icons.add, loc.add, () => agg.$state.go('kb.quoteNew'));
        if ($rootScope.companySettings.enableQuoteImport) {
            agg.drawerService.addDrawer(icons.upload, 'Import', () => this.import());
        }
        agg.drawerService.addDrawer(icons.clone, loc.clone, () => this.clone());
        agg.drawerService.addDrawer(icons.deleter, loc.deleter, () => this.deleteItems());
    }

    public import() {
        this.upload.promptUserToChooseFile().then(file => {
            let formData = new FormData();
            formData.append('file', file);
            this.upload.uploadFile(formData, '/api/quotes/import').then(r => {
                this.agg.$state.go('kb.quoteEdit', { id: r.id });
            });
        });
    }

    public client(api: ApiService) {
        return api.quotes;
    }
    public newSearch(): ISavedSearch {
        return {
            modelType: meta.Quote.$name,
            sortField: meta.Quote.modifiedDate.name, // "modifiedDate",
            descending: true,
            filters: this.getFiltersFromMeta(meta.Quote.$name, true),
            roles: [],
        };
    }

    protected onItemsDeleted(items: IQuote[]) {
        //empty out the active quote if we just deleted it
        for (let q of items) {
            if (q && this.quoteService.quote && q.id == this.quoteService.quote.id) {
                this.quoteService.quote = null;
            }
        }
    }

    public getWhereFieldFilter(): IFilter {
        return {
            type: eFilterType.headerValue,
            label: 'Where Header Field',
            fieldName: null,
            control: eFilterControl.none,
            sourceType: eFilterSource.headerFields,
            values: [null],
        };
    }
}
